import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Zap, ZapOff, Globe, Play } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Button } from "../ui/button";
import AddWebTargetDialog from "./AddWebTargetDialog";
import RunWebTargetDialog from "./RunWebTargetDialog";

export default function GatewayWebTargetsList({ disabled }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [running, setRunning] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/gateway/web-targets");
      setItems(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disabled) { setLoading(false); return; }
    refresh();
  }, [disabled]);

  const remove = async (id) => {
    if (!window.confirm(`¿Eliminar el web target '${id}'?`)) return;
    try {
      await api.delete(`/gateway/web-targets/${id}`);
      toast.success("Web target eliminado");
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "web_target_has_routes") {
        const go = window.confirm(
          `${d.message}\nAliases: ${d.aliases.join(", ")}\n\n¿Eliminar en cascada?`
        );
        if (go) {
          try {
            await api.delete(`/gateway/web-targets/${id}?force=true`);
            toast.success("Web target y rutas eliminados");
            refresh();
          } catch (e2) { toast.error(apiErrorMessage(e2)); }
        }
        return;
      }
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          Web Targets referencian perfiles y servicios web existentes — no duplican datos.
        </div>
        <Button
          data-testid={TID.gateway.addWebTargetBtn}
          onClick={() => setEditing("new")}
          disabled={disabled}
          size="sm"
          className="gap-2 bg-white text-black hover:bg-zinc-200"
        >
          <Plus className="h-3.5 w-3.5" /> Nuevo Web Target
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {loading ? (
          <div className="text-xs text-zinc-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="col-span-2 rounded-md border border-dashed border-zinc-800 p-8 text-center text-xs text-zinc-500">
            Aún no has creado ningún Web Target.
          </div>
        ) : items.map((t) => (
          <div
            key={t.id}
            data-testid={TID.gateway.webTargetRow(t.id)}
            className="rounded-md border border-zinc-800 bg-zinc-950 p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Globe className="h-3.5 w-3.5 text-zinc-500" />
                  <span className="truncate">{t.name}</span>
                </div>
                <div className="text-[11px] text-zinc-500 font-mono truncate">
                  {t.id} · {t.executor}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  data-testid={TID.gateway.webTargetRun(t.id)}
                  onClick={() => setRunning(t)}
                  disabled={!t.active}
                  title={t.active ? "Ejecutar prompt" : "Web target inactivo"}
                  className="p-1.5 text-zinc-400 hover:text-emerald-400 rounded disabled:opacity-40 disabled:hover:text-zinc-400"
                >
                  <Play className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={TID.gateway.webTargetEdit(t.id)}
                  onClick={() => setEditing(t)}
                  className="p-1.5 text-zinc-400 hover:text-white rounded"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={TID.gateway.webTargetDelete(t.id)}
                  onClick={() => remove(t.id)}
                  className="p-1.5 text-zinc-400 hover:text-red-400 rounded"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="rounded border border-zinc-800/70 bg-black/30 p-2.5 text-[11px] font-mono text-zinc-400 space-y-1">
              <div>URL: <span className="text-zinc-300 truncate block">{t.target_url}</span></div>
              <div>Perfil: <span className="text-zinc-300">{t.browser_profile}</span></div>
              <div>Prompt sel: <span className="text-zinc-300 truncate block">{t.prompt_selector}</span></div>
              <div>Response sel: <span className="text-zinc-300 truncate block">{t.response_selector}</span></div>
            </div>

            {Array.isArray(t.capabilities) && t.capabilities.length > 0 && (
              <div
                data-testid={`gateway-web-target-caps-${t.id}`}
                className="flex flex-wrap gap-1 pt-0.5"
              >
                {t.capabilities.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center rounded border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-mono text-emerald-300"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <span className={
                "inline-flex items-center gap-1.5 text-[11px] " +
                (t.active ? "text-emerald-400" : "text-zinc-500")
              }>
                {t.active ? <Zap className="h-3 w-3" /> : <ZapOff className="h-3 w-3" />}
                {t.active ? "Activo" : "Inactivo"}
              </span>
              <span className="text-[10px] text-zinc-600 font-mono">
                {new Date(t.updated_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <AddWebTargetDialog
          target={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {running && (
        <RunWebTargetDialog
          target={running}
          onClose={() => setRunning(null)}
        />
      )}
    </div>
  );
}
